import { type JSX, ReactNode } from 'react';
import { useParams } from 'react-router';

import { Button } from 'Components/Buttons/Buttons';
import { Text } from 'Components/Text/Text';
import { ICON_SIGN_IN } from 'Config/Icons';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { AuthState } from 'Models/Auth';

import styles from './LogOut.module.css';

interface LogOutProps {
    children: ReactNode;
    logoutButton?: ReactNode;
}

const LogOut = (props: LogOutProps) => {
    return (
        <div className={styles.logOutBackground}>
            <div className={styles.logOutContainer}>
                <div className={styles.logOutTitle}>
                    <Text variant="Header1" color="white">
                        {props.children}
                    </Text>
                </div>
                {props.logoutButton}
            </div>
        </div>
    );
};

interface LogOutType {
    error?: string;
}
export interface ClientLogOutProps {
    authHandler: AuthHandler;
}

/**
 * Screen to be displayed when logging out a SummIT client. This occurs when the user explicitly logs out, or when the API returns a 401 response.
 *
 * It has a login button to enable the user to log back in.
 *
 * A sensible question is: If we want a seamless experience for the user, why show the Logout screen and require clicking the Login button at all?
 * We do this because Logouts should be explicit, for security reasons.
 * One scenario that leads to a 401 response is a second browser logging in as the same user, which de-auths the session in the original browser.
 * In this scenario, it is important that the user knows that a logout occurred.
 */
export const ClientLogOut = (props: ClientLogOutProps): JSX.Element => {
    const { error } = useParams<keyof LogOutType>() as LogOutType;

    let errorMessage: string;

    switch (error) {
        case AuthState.UNAUTHENTICATED:
            errorMessage = 'You must be authenticated to access SummIT.';
            break;
        case AuthState.LOGGED_OUT:
            errorMessage = 'Successfully logged out of SummIT.';
            break;
        case AuthState.TIMEOUT:
            errorMessage = 'Your SummIT session has timed out.';
            break;
        default:
            errorMessage = 'An unexpected error has occurred.';
            break;
    }

    return (
        <LogOut
            logoutButton={
                <Button variant="primary" onClick={props.authHandler.logIn} fontAwesomeImage={ICON_SIGN_IN}>
                    Log In
                </Button>
            }
        >
            {errorMessage}
        </LogOut>
    );
};
